import { useState, useEffect, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { DP_ID_RE, IFSC_RE, PAN_RE, NOMINEE_RELATIONSHIPS, sanitizeAlphaSpace, sanitizeName, ddmmyyyyToISO, isoToDDMMYYYY } from '@new-wealth/shared';
import { api, ApiError } from '../api/client.js';
import { ReferredByPicker } from './ReferredByPicker.js';

/**
 * Staff customer-enrolment wizard (modal). Mirrors the old app's 6-section
 * flow — Personal · Demat · KYC docs · Bank · Nominee · Review. Fields are
 * persisted to the customer (create → demat → KYC docs → bank → nominee) only
 * on "Save & exit" / "Save & add investment", so Back/Next never duplicates
 * rows. The customer is created live — no separate approval (owner 2026-07-21);
 * the investment is the single approval gate.
 *
 * Google-Sheets-style safety net: as you type, the text fields autosave to the
 * browser (localStorage, debounced) and are restored if the modal is closed or
 * reopened — so a stray outside-click no longer loses your work. Clearing
 * happens on a successful save/submit or via "Start fresh". (File picks can't
 * be persisted by the browser, so only those re-attach after a full close.)
 */

const GENDERS = ['Male', 'Female', 'Other'];
const CATEGORIES = ['Individual', 'HUF', 'Corporate', 'Trust', 'NRI', 'Others'];
const DEPOSITORIES = ['NSDL', 'CDSL'];
const ACCOUNT_TYPES = ['Savings', 'Current'];
const RELATIONSHIPS = NOMINEE_RELATIONSHIPS;

const STEPS = ['Personal', 'Demat', 'KYC docs', 'Bank', 'Nominee', 'Review'] as const;

const inp = 'px-2.5 py-1.5 text-sm border border-border-strong rounded outline-none focus:border-primary w-full';

const EMPTY = {
  // Personal
  full_name: '', father_name: '', occupation: '', dob: '', gender: '', pan: '', aadhaar: '',
  phone: '', phone_secondary: '', email: '', investor_category: '',
  address: '', pincode: '', city: '', district: '', state: '', is_nri: false, referred_by_text: '',
  // Demat
  depository: '', dp_id: '', client_id: '',
  // KYC
  ckyc_number: '',
  // Bank
  bank_holder_name: '', account_number: '', account_type: '', tds: '', ifsc: '',
  bank_name: '', branch_name: '', branch_city: '',
  // Nominee (KYC replaces the old PAN-only field: Aadhaar or PAN + a photo)
  nom_full_name: '', nom_dob: '', nom_relationship: '', nom_kyc_type: '', nom_kyc_number: '', nom_phone: '',
  nom_address: '', guardian_name: '', guardian_pan: '',
};
type Form = typeof EMPTY;
type DocKey = 'pan_card' | 'aadhaar_card' | 'customer_photo' | 'customer_signature' | 'address_proof' | 'cml' | 'bank_proof' | 'nominee_kyc' | 'form_121';

// Must match the backend cap (lib/uploads MAX_UPLOAD_BYTES). Checked BEFORE the
// customer is created, so an oversized scan is refused up-front instead of after
// the customer commits (which orphaned them without docs — owner 2026-09-09).
const MAX_DOC_MB = 10;
const MAX_DOC_BYTES = MAX_DOC_MB * 1024 * 1024;

// Autosave the in-progress enrolment to the browser so an accidental close /
// navigation never loses typed data. Files aren't serialisable, so only text
// fields survive a full close.
const DRAFT_KEY = 'ncd:enroll-draft-v1';
function loadDraft(): { f?: Partial<Form>; step?: number } | null {
  try { const s = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); return s && s.f ? s : null; } catch { return null; }
}
function isDirty(f: Form): boolean {
  return (Object.keys(EMPTY) as Array<keyof Form>).some((k) => f[k] !== EMPTY[k]);
}

function readBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] ?? '');
    r.onerror = () => reject(new Error('read failed'));
    r.readAsDataURL(file);
  });
}

/** Whole-year age from a DD/MM/YYYY DOB, or null. Senior citizen = 60+. */
function ageFromDob(dob: string): number | null {
  const iso = ddmmyyyyToISO(dob);
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a >= 0 && a < 130 ? a : null;
}

/** DOB is typed as DD/MM/YYYY — digits only, slashes inserted as you type. */
function formatDobInput(v: string): string {
  const digits = v.replace(/\D/g, '').slice(0, 8);
  if (digits.length > 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
}

/**
 * Normalise values entering the form from outside the typing guards — a lead
 * prefill, a resumed draft, or the PIN autofill: alpha-space fields drop
 * invalid characters (the API enforces the same rule) and an ISO dob from an
 * old draft converts to the DD/MM/YYYY display format.
 */
function normalizeIncoming(f: Form): Form {
  const out = { ...f };
  for (const k of ['full_name', 'father_name', 'bank_holder_name', 'bank_name', 'branch_name', 'branch_city', 'nom_full_name'] as const) out[k] = sanitizeName(out[k]);
  for (const k of ['occupation', 'city', 'district', 'state'] as const) out[k] = sanitizeAlphaSpace(out[k]);
  out.account_number = out.account_number.replace(/\D/g, '');
  if (out.dob && /^\d{4}-\d{2}-\d{2}/.test(out.dob)) out.dob = isoToDDMMYYYY(out.dob);
  return out;
}

/** Depository from a DP ID: NSDL DP IDs start with "IN"; CDSL are 8-digit numeric. */
function depositoryFromDpId(dp: string): string {
  const v = dp.trim().toUpperCase();
  if (v.startsWith('IN')) return 'NSDL';
  if (/^\d{8}$/.test(v)) return 'CDSL';
  return '';
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-text-label uppercase tracking-wide">{label}{required && <span className="text-danger"> *</span>}</span>
      {children}
      {hint && <span className="text-[11px] text-text-muted">{hint}</span>}
    </label>
  );
}

function FilePick({ label, hint, file, onPick }: { label: string; hint?: string; file: File | null; onPick: (f: File | null) => void }) {
  return (
    <div className="bg-bg border border-border rounded p-3">
      <div className="text-sm font-semibold">{label}</div>
      {hint && <div className="text-xs text-text-muted mb-2">{hint}</div>}
      <input type="file" accept="image/*,application/pdf" className="text-xs w-full"
        onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
      {file && <div className="text-[11px] text-success mt-1">✓ {file.name}</div>}
    </div>
  );
}



/** Pull a readable field reason out of an API error's `detail`.
 *  The API sends zod's `flatten()` — `{ formErrors, fieldErrors }` — NOT the
 *  raw issue list. Reading the wrong shape is why the first attempt at this
 *  showed nothing; a test caught it. `issues` is kept as a fallback in case a
 *  route ever returns the unflattened form. */
function fieldReason(detail: unknown): string {
  if (!detail || typeof detail !== 'object') return typeof detail === 'string' ? detail : '';
  const d = detail as {
    fieldErrors?: Record<string, string[] | undefined>;
    formErrors?: string[];
    issues?: Array<{ path?: unknown[]; message?: string }>;
  };
  const parts: string[] = [];
  for (const [field, msgs] of Object.entries(d.fieldErrors ?? {})) {
    if (msgs?.length) parts.push(`${field}: ${msgs[0]}`);
  }
  if (!parts.length && d.formErrors?.length) parts.push(d.formErrors[0]!);
  if (!parts.length && Array.isArray(d.issues)) {
    for (const i of d.issues) {
      const f = Array.isArray(i.path) && i.path.length ? String(i.path[i.path.length - 1]) : '';
      parts.push(f ? `${f}: ${i.message ?? 'invalid'}` : (i.message ?? 'invalid'));
    }
  }
  return parts.join('; ');
}

export function CustomerWizard(
  { onClose, prefill, onCreated }: {
    onClose: () => void;
    // Seed the form (e.g. converting a lead — name/phone already known).
    prefill?: Partial<Form>;
    // Called with the new customer id after a successful save (e.g. to mark the
    // originating lead Converted). Awaited so its failure surfaces in the wizard.
    onCreated?: (customerId: number) => Promise<void> | void;
  }
) {
  const nav = useNavigate();
  const qc = useQueryClient();
  // A prefill (lead conversion) starts a FRESH form — it must not resurrect an
  // unrelated autosaved enrolment draft, and it isn't the resumable draft.
  const [step, setStep] = useState<number>(() => (prefill ? 0 : loadDraft()?.step ?? 0));
  const [f, setF] = useState<Form>(() => {
    if (prefill) return normalizeIncoming({ ...EMPTY, ...prefill });
    const d = loadDraft(); return d?.f ? normalizeIncoming({ ...EMPTY, ...d.f }) : EMPTY;
  });
  const [restored, setRestored] = useState<boolean>(() => !prefill && !!loadDraft());
  const [files, setFiles] = useState<Record<DocKey, File | null>>({
    pan_card: null, aadhaar_card: null, customer_photo: null, customer_signature: null, address_proof: null, cml: null, bank_proof: null, nominee_kyc: null, form_121: null,
  });
  const [err, setErr] = useState('');
  const [dup, setDup] = useState<{ id: number; customer_code: string; full_name: string } | null>(null);
  // The customer was created but an attachment (doc/bank/nominee) failed — shown
  // so the operator adds the rest on the profile instead of re-saving into a 409.
  const [partial, setPartial] = useState<{ id: number; code: string; reason: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [pinState, setPinState] = useState<'idle' | 'looking' | 'ok' | 'miss'>('idle');
  const [ifscState, setIfscState] = useState<'idle' | 'looking' | 'ok' | 'miss'>('idle');
  const [penny, setPenny] = useState<{ status: string; name?: string | null; detail?: string } | null>(null);
  const [pennyBusy, setPennyBusy] = useState(false);
  const set = (patch: Partial<Form>) => setF((prev) => ({ ...prev, ...patch }));
  const setFile = (k: DocKey, file: File | null) => setFiles((prev) => ({ ...prev, [k]: file }));
  // The hidden native date input behind the DOB calendar button (showPicker()).
  const dobPickerRef = useRef<HTMLInputElement>(null);

  // PIN → city/state autofill (India Post). Non-blocking: a miss leaves the
  // fields editable. Fires when the PIN reaches 6 digits.
  async function onPincode(v: string) {
    const pin = v.replace(/\D/g, '').slice(0, 6);
    set({ pincode: pin });
    if (pin.length !== 6) { setPinState('idle'); return; }
    setPinState('looking');
    try {
      const r = await api.get<{ found: boolean; state?: string; city?: string }>(`/api/lookups/pincode/${pin}`);
      // India Post names can carry punctuation ("Basti (Jalandhar)") — the
      // fields are letters-and-spaces only, so sanitise the autofill too.
      if (r.found) { set({ state: sanitizeAlphaSpace(r.state ?? ''), city: sanitizeAlphaSpace(r.city ?? ''), district: sanitizeAlphaSpace(r.city ?? '') }); setPinState('ok'); }
      else setPinState('miss');
    } catch { setPinState('miss'); }
  }

  // IFSC → bank/branch autofill (debounced), mirroring the bank-account form.
  useEffect(() => {
    const code = f.ifsc.trim().toUpperCase();
    if (!IFSC_RE.test(code)) { setIfscState('idle'); return; }
    let cancelled = false;
    setIfscState('looking');
    const t = setTimeout(async () => {
      try {
        const r = await api.get<{ found: boolean; bank?: string; branch?: string; city?: string }>(`/api/lookups/ifsc/${code}`);
        if (cancelled) return;
        // Lookup data can carry digits/punctuation ("Sector 17") — these fields
        // take the person-name rule, so the autofill is sanitised the same way.
        if (r.found) { set({ bank_name: sanitizeName(r.bank ?? ''), branch_name: sanitizeName(r.branch ?? ''), branch_city: sanitizeName(r.city ?? '') }); setIfscState('ok'); }
        else setIfscState('miss');
      } catch { if (!cancelled) setIfscState('miss'); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [f.ifsc]);

  async function runPennyDrop() {
    setPenny(null); setPennyBusy(true);
    try {
      const r = await api.post<{ status: string; name_on_record: string | null; detail: string }>('/api/lookups/penny-drop', {
        account_number: f.account_number.trim(), ifsc: f.ifsc.trim().toUpperCase(), name: f.bank_holder_name.trim() || undefined,
      });
      setPenny({ status: r.status, name: r.name_on_record, detail: r.detail });
      if (r.status === 'Verified' && r.name_on_record && !f.bank_holder_name.trim()) set({ bank_holder_name: sanitizeName(r.name_on_record) });
    } catch (e) { setPenny({ status: 'Failed', detail: e instanceof ApiError ? e.message : 'Verification failed' }); }
    finally { setPennyBusy(false); }
  }

  // Debounced autosave of the text fields (Google-Sheets-style) so an accidental
  // close never loses typed data; cleared on a successful save or "Start fresh".
  useEffect(() => {
    const t = setTimeout(() => {
      const dirty = isDirty(f);
      try {
        if (dirty) localStorage.setItem(DRAFT_KEY, JSON.stringify({ f, step }));
        else localStorage.removeItem(DRAFT_KEY);
      } catch { /* storage disabled / quota — best-effort */ }
      // Mirror to the server (owner 2026-08-22) so the draft survives a browser
      // change AND a super-admin can see every user's in-progress enrolment.
      // Best-effort — a failed save must never interrupt typing.
      if (dirty) void api.put('/api/customers/drafts', {
        draft: { f, step }, display_name: f.full_name?.trim() || null, display_phone: f.phone?.trim() || null,
      }).catch(() => { /* ignore */ });
    }, 500);
    return () => clearTimeout(t);
  }, [f, step]);

  // Restore a server-side draft when there's nothing local (a different machine,
  // or localStorage cleared). Local/prefill still win when present.
  useEffect(() => {
    if (prefill || loadDraft()) return;
    let cancelled = false;
    void api.get<{ draft?: { f?: Partial<Form>; step?: number } } | null>('/api/customers/drafts/mine')
      .then((r) => { if (!cancelled && r?.draft?.f) { setF({ ...EMPTY, ...r.draft.f }); setStep(r.draft.step ?? 0); setRestored(true); } })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
    void api.del('/api/customers/drafts/mine').catch(() => { /* ignore */ });
  };
  const startFresh = () => {
    clearDraft();
    setF(EMPTY); setStep(0); setRestored(false); setErr(''); setDup(null);
    setFiles({ pan_card: null, aadhaar_card: null, customer_photo: null, customer_signature: null, address_proof: null, cml: null, bank_proof: null, nominee_kyc: null, form_121: null });
  };

  function buildPersonal(): Record<string, unknown> {
    // TDS is an explicit Yes/No the operator picks (no default) — finish()
    // blocks submit until one is chosen, so f.tds is 'yes' or 'no' here.
    const personal: Record<string, unknown> = { full_name: f.full_name.trim(), is_nri: f.is_nri, tds_applicable: f.tds === 'yes' };
    const put = (k: string, v: string) => { if (v.trim()) personal[k] = v.trim(); };
    // The form takes DOB as DD/MM/YYYY; the API stores ISO.
    put('pan', f.pan); put('dob', ddmmyyyyToISO(f.dob) ?? ''); put('gender', f.gender); put('phone', f.phone);
    put('email', f.email); put('address', f.address); put('pincode', f.pincode); put('city', f.city); put('district', f.district); put('state', f.state);
    put('referred_by_text', f.referred_by_text); put('father_name', f.father_name); put('occupation', f.occupation);
    put('phone_secondary', f.phone_secondary); put('investor_category', f.investor_category); put('ckyc_number', f.ckyc_number);
    // Send the FULL Aadhaar when all 12 are entered (owner 2026-07-21: stored and
    // printed on the application form) — the backend derives last-4 from it.
    // Previously this only ever sent the last 4, so the full number the operator
    // typed was thrown away and the form printed blanks.
    const aadhaarDigits = f.aadhaar.replace(/\D/g, '');
    if (aadhaarDigits.length === 12) personal.aadhaar = aadhaarDigits;
    else if (aadhaarDigits.length >= 4) personal.aadhaar_last4 = aadhaarDigits.slice(-4);
    return personal;
  }

  // Everything that hangs off the customer — demat, KYC scans, bank, nominee.
  // Each needs the customer id, so this runs AFTER the row exists; a failure here
  // leaves a created customer missing some of these, which finish() turns into a
  // "created — add the rest" message rather than a bare failure + duplicate retry.
  async function attachExtras(id: number) {
    if (f.dp_id.trim() || f.client_id.trim() || f.depository)
      await api.put(`/api/customers/${id}/demat`, { dp_id: f.dp_id.trim(), client_id: f.client_id.trim(), depository: f.depository || null });

    for (const [doc_type, file] of Object.entries(files)) {
      if (!file) continue;
      const data_base64 = await readBase64(file);
      if (data_base64) await api.post(`/api/customers/${id}/documents`, { doc_type, filename: file.name, mime: file.type || 'application/octet-stream', data_base64 });
    }

    if (f.account_number.trim() && f.ifsc.trim())
      await api.post(`/api/customers/${id}/bank-accounts`, {
        account_number: f.account_number.trim(), ifsc: f.ifsc.trim().toUpperCase(),
        bank_name: f.bank_name.trim() || undefined, branch_name: f.branch_name.trim() || undefined, branch_city: f.branch_city.trim() || undefined,
        account_type: f.account_type || undefined, holder_name: f.bank_holder_name.trim() || undefined, tds_applicable: f.tds !== 'no',
      });

    if (f.nom_full_name.trim())
      await api.put(`/api/customers/${id}/nominees`, { nominees: [{
        full_name: f.nom_full_name.trim(), dob: f.nom_dob || null, relationship: f.nom_relationship || null,
        kyc_id_type: f.nom_kyc_type || null, kyc_id_number: f.nom_kyc_number.trim() || null,
        phone: f.nom_phone.trim() || null, address: f.nom_address.trim() || null,
        guardian_name: f.guardian_name.trim() || null, guardian_pan: f.guardian_pan.trim() || null,
      }] });
  }

  // Customer is created live (no approval — owner 2026-07-21). goInvest → open
  // their profile, where the "Add investment" form is ready; else just close.
  async function finish(goInvest: boolean) {
    // Mirrors the API schema (shared/validation) so the operator gets the
    // message here instead of a request round-trip.
    if (!f.full_name.trim()) { setErr('Full name is required.'); setStep(0); return; }
    if (f.dob.trim() && !ddmmyyyyToISO(f.dob)) { setErr('Date of birth must be a valid date in DD/MM/YYYY format.'); setStep(0); return; }
    if (f.pan && !PAN_RE.test(f.pan)) { setErr('PAN must be in the format ABCDE1234F — 5 letters, 4 digits, then a letter.'); setStep(0); return; }
    if (f.dp_id.trim() && !DP_ID_RE.test(f.dp_id.trim())) { setErr('DP ID must be 8 characters — two letters + six digits (e.g. IN300456) or eight digits (CDSL).'); setStep(1); return; }
    if (f.account_number.trim() && f.ifsc.trim() && !IFSC_RE.test(f.ifsc.trim())) { setErr('IFSC must be 11 characters like SBIN0001234.'); setStep(3); return; }
    if (f.tds !== 'yes' && f.tds !== 'no') { setErr('Please choose whether TDS applies on interest payouts — Yes or No.'); setStep(3); return; }
    // Refuse an oversized scan BEFORE the customer is created. Otherwise the
    // customer commits, the doc upload then fails on size, and the operator is
    // left with a customer missing its docs plus a "save again" that hits
    // "already exists" (owner 2026-09-09).
    const big = Object.values(files).find((file) => file && file.size > MAX_DOC_BYTES);
    if (big) { setErr(`"${big.name}" is ${(big.size / (1024 * 1024)).toFixed(1)} MB — each document must be under ${MAX_DOC_MB} MB. Replace it with a smaller scan, then save.`); setStep(2); return; }
    setErr(''); setDup(null); setPartial(null); setBusy(true);
    // Kept in scope so the catch can tell "the customer never got created"
    // (a duplicate / validation error to retry) from "created, but an attachment
    // failed" (nothing to retry — the row exists).
    let created: { id: number; customer_code: string } | null = null;
    try {
      created = await api.post<{ id: number; customer_code: string }>('/api/customers', buildPersonal());
      await attachExtras(created.id);
      clearDraft(); // saved server-side now — drop the local autosave
      // Link the originating lead (if any) BEFORE we navigate away, so a link
      // failure is shown here rather than lost.
      if (onCreated) await onCreated(created.id);
      qc.invalidateQueries({ queryKey: ['customers'] });
      onClose();
      if (goInvest) nav(`/app/customers/${created.id}`);
    } catch (e) {
      if (created) {
        // The customer IS saved; an attachment step (doc / bank / nominee) failed.
        // Re-saving would only 409, so drop the draft and point the operator at the
        // profile to add whatever didn't attach — instead of a bare "Save failed".
        clearDraft();
        qc.invalidateQueries({ queryKey: ['customers'] });
        setPartial({ id: created.id, code: created.customer_code,
          reason: e instanceof ApiError ? [e.message, fieldReason(e.detail)].filter(Boolean).join(' — ') : 'the upload failed' });
        return;
      }
      const d = e instanceof ApiError ? (e.detail as { existing_customer?: { id: number; customer_code: string; full_name: string } } | undefined) : undefined;
      if (d?.existing_customer) setDup(d.existing_customer);
      // The API puts the FIELD-LEVEL reason in `detail` and leaves `message` as
      // a generic "Invalid request". Showing only the message told the operator
      // nothing — three identical 400s in a row, no way to know which field
      // (owner 2026-08-24). Surface both, the way the locker page does.
      setErr(e instanceof ApiError
        ? [e.message, fieldReason(e.detail)].filter(Boolean).join(' — ')
        : 'Save failed');
    } finally { setBusy(false); }
  }

  // Backdrop does NOT close on click — a stray outside-click must never discard
  // an in-progress enrolment. Close via ✕ / Cancel (the draft autosaves anyway).
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center overflow-y-auto py-6 px-4">
      <div className="bg-surface border border-border rounded-lg shadow-lg w-full max-w-3xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border sticky top-0 bg-surface z-10">
          <h2 className="text-base font-bold m-0">Enroll customer</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text text-lg leading-none" aria-label="Close">✕</button>
        </div>

        {restored && (
          <div className="flex items-center justify-between gap-2 px-5 py-2 border-b border-border bg-[color:var(--success-bg,#f0fdf4)] text-xs">
            <span className="text-text-muted">↩ Resumed your unsaved draft — it autosaves as you type.</span>
            <button onClick={startFresh} className="text-primary hover:underline font-medium whitespace-nowrap">Start fresh</button>
          </div>
        )}

        {/* Step strip */}
        <div className="flex flex-wrap gap-1 px-5 py-3 border-b border-border bg-bg/50">
          {STEPS.map((label, i) => (
            <button key={label} onClick={() => setStep(i)}
              className={`text-xs rounded-full px-3 py-1 flex items-center gap-1.5 ${i === step ? 'bg-primary text-white' : i < step ? 'text-success' : 'text-text-muted'}`}>
              <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] ${i === step ? 'bg-white/20' : i < step ? 'bg-[color:var(--success-bg)]' : 'bg-border'}`}>{i < step ? '✓' : i + 1}</span>
              {label}
            </button>
          ))}
        </div>

        <div className="p-5 max-h-[65vh] overflow-y-auto">
          {step === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              {/* Names allow letters, spaces and . ' - (never digits); occupation and
                  place fields are letters-and-spaces only — invalid characters are
                  dropped as they're typed (same rules enforced by the API). */}
              <div className="sm:col-span-2"><Field label="Full name" required><input className={inp} value={f.full_name} onChange={(e) => set({ full_name: sanitizeName(e.target.value) })} autoFocus /></Field></div>
              <Field label="Father's name"><input className={inp} value={f.father_name} onChange={(e) => set({ father_name: sanitizeName(e.target.value) })} /></Field>
              <Field label="Occupation" hint="e.g. Salaried, Business, Retired"><input className={inp} value={f.occupation} onChange={(e) => set({ occupation: sanitizeAlphaSpace(e.target.value) })} /></Field>
              <Field label="Date of birth" hint="DD/MM/YYYY">
                {/* Text entry shows DD/MM/YYYY always (a native date input's display
                    format follows the OS locale and can't be forced); the 📅 button
                    opens a REAL native picker via a visually-hidden date input whose
                    pick is written back as DD/MM/YYYY. API still receives ISO. */}
                <div className="relative">
                  <input className={`${inp} pr-9`} inputMode="numeric" placeholder="DD/MM/YYYY" maxLength={10} value={f.dob} onChange={(e) => set({ dob: formatDobInput(e.target.value) })} />
                  <input ref={dobPickerRef} type="date" tabIndex={-1} aria-label="Pick date of birth from calendar"
                    className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 opacity-0"
                    value={ddmmyyyyToISO(f.dob) ?? ''}
                    onChange={(e) => set({ dob: e.target.value ? isoToDDMMYYYY(e.target.value) : '' })} />
                  <button type="button" title="Open calendar" aria-hidden tabIndex={-1}
                    className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 text-text-muted hover:text-text"
                    onClick={() => { const el = dobPickerRef.current; try { el?.showPicker(); } catch { el?.focus(); } }}>📅</button>
                </div>
                {f.dob.length === 10 && !ddmmyyyyToISO(f.dob) && <span className="text-[11px] text-danger">Not a valid date — use DD/MM/YYYY.</span>}
              </Field>
              <Field label="Age / category" hint="Auto from DOB; senior citizen = 60+ (drives TDS filing category)">
                <div className={`${inp} flex items-center gap-2 bg-bg`}>
                  {ageFromDob(f.dob) != null ? (<>
                    <span className="font-semibold">{ageFromDob(f.dob)} yrs</span>
                    <span className={`text-[11px] rounded px-1.5 py-0.5 ${ageFromDob(f.dob)! >= 60 ? 'bg-[color:var(--warn-bg)] text-warn' : 'bg-border text-text-muted'}`}>{ageFromDob(f.dob)! >= 60 ? 'Senior citizen' : 'General'}</span>
                  </>) : <span className="text-text-muted">—</span>}
                </div>
              </Field>
              <Field label="Gender"><select className={inp} value={f.gender} onChange={(e) => set({ gender: e.target.value })}><option value="">—</option>{GENDERS.map((g) => <option key={g}>{g}</option>)}</select></Field>
              <Field label="PAN">
                <input className={`${inp} uppercase`} placeholder="ABCDE1234F" maxLength={10} value={f.pan} onChange={(e) => set({ pan: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) })} />
                {f.pan.length === 10 && !PAN_RE.test(f.pan) && <span className="text-[11px] text-danger">PAN must be ABCDE1234F — 5 letters, 4 digits, then a letter.</span>}
              </Field>
              <Field label="Aadhaar (12 digits)" hint="Enter all 12 digits — the full Aadhaar is stored and printed on the application form."><input className={inp} inputMode="numeric" maxLength={12} placeholder="Full 12-digit Aadhaar" value={f.aadhaar} onChange={(e) => set({ aadhaar: e.target.value.replace(/\D/g, '') })} /></Field>
              <Field label="Phone (primary)"><input className={inp} inputMode="numeric" maxLength={10} value={f.phone} onChange={(e) => set({ phone: e.target.value.replace(/\D/g, '') })} /></Field>
              <Field label="Phone (secondary)"><input className={inp} inputMode="numeric" maxLength={10} value={f.phone_secondary} onChange={(e) => set({ phone_secondary: e.target.value.replace(/\D/g, '') })} /></Field>
              <Field label="Email"><input className={inp} type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} /></Field>
              <Field label="Investor category"><select className={inp} value={f.investor_category} onChange={(e) => set({ investor_category: e.target.value })}><option value="">—</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
              <div className="sm:col-span-2"><Field label="Address"><input className={inp} value={f.address} onChange={(e) => set({ address: e.target.value })} /></Field></div>
              <Field label="Pincode" hint={pinState === 'looking' ? 'Looking up…' : pinState === 'ok' ? '✓ City/State filled — edit if needed' : pinState === 'miss' ? 'Not found — enter city/state manually' : 'Auto-fills city & state'}>
                <input className={inp} inputMode="numeric" maxLength={6} placeholder="6-digit PIN" value={f.pincode} onChange={(e) => onPincode(e.target.value)} />
              </Field>
              <Field label="City"><input className={inp} value={f.city} onChange={(e) => set({ city: sanitizeAlphaSpace(e.target.value) })} /></Field>
              <Field label="District"><input className={inp} value={f.district} onChange={(e) => set({ district: sanitizeAlphaSpace(e.target.value) })} /></Field>
              <Field label="State"><input className={inp} value={f.state} onChange={(e) => set({ state: sanitizeAlphaSpace(e.target.value) })} /></Field>
              <Field label="Referred by" hint="Pick an agent/staff code — or type a new name (becomes an agent after approval)"><ReferredByPicker value={f.referred_by_text} onChange={(v) => set({ referred_by_text: v })} /></Field>
              <label className="text-xs flex items-center gap-1.5 mt-1"><input type="checkbox" checked={f.is_nri} onChange={(e) => set({ is_nri: e.target.checked })} />NRI</label>
            </div>
          )}

          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              <p className="sm:col-span-2 text-xs text-text-muted -mb-1">DP ID and Client ID are 8 characters each (NSDL/CDSL standard). Optional at this stage.</p>
              <Field label="Depository"><select className={inp} value={f.depository} onChange={(e) => set({ depository: e.target.value })}><option value="">—</option>{DEPOSITORIES.map((d) => <option key={d}>{d}</option>)}</select></Field>
              <div />
              <Field label="DP ID (8 chars)" hint="NSDL starts with IN; 8-digit numeric = CDSL — depository auto-fills">
                <input className={`${inp} uppercase`} placeholder="e.g. IN300456" maxLength={8} value={f.dp_id} onChange={(e) => { const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8); const dep = depositoryFromDpId(v); set(dep ? { dp_id: v, depository: dep } : { dp_id: v }); }} />
                {f.dp_id.length === 8 && !DP_ID_RE.test(f.dp_id) && <span className="text-[11px] text-danger">DP ID must be two letters + six digits (e.g. IN300456) or eight digits (CDSL).</span>}
              </Field>
              <Field label="Client ID (8 digits)"><input className={inp} inputMode="numeric" placeholder="e.g. 12345678" maxLength={8} value={f.client_id} onChange={(e) => set({ client_id: e.target.value.replace(/\D/g, '').slice(0, 8) })} /></Field>
              <div className="sm:col-span-2"><FilePick label="CML copy" hint="Client Master List from depository — PDF or image scan" file={files.cml} onPick={(x) => setFile('cml', x)} /></div>
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-xs text-text-muted mb-3">Upload PDF or image scans (max 10 MB each). All documents are optional at this stage — they can be collected later.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FilePick label="PAN card" hint="Front side, clear scan" file={files.pan_card} onPick={(x) => setFile('pan_card', x)} />
                <FilePick label="Aadhaar card" hint="Both sides / e-Aadhaar PDF" file={files.aadhaar_card} onPick={(x) => setFile('aadhaar_card', x)} />
                <FilePick label="Customer photo" hint="Passport-size, clear face" file={files.customer_photo} onPick={(x) => setFile('customer_photo', x)} />
                <FilePick label="Customer signature" hint="On white paper, scanned" file={files.customer_signature} onPick={(x) => setFile('customer_signature', x)} />
                <FilePick label="Address proof" hint="Aadhaar / Voter ID / utility bill" file={files.address_proof} onPick={(x) => setFile('address_proof', x)} />
              </div>
              <div className="mt-3 max-w-sm"><Field label="CKYC number (optional)"><input className={inp} value={f.ckyc_number} onChange={(e) => set({ ckyc_number: e.target.value })} /></Field></div>
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              <p className="sm:col-span-2 text-xs text-text-muted -mb-1">Where monthly interest lands. Enter an account + IFSC to save it now, or add it later.</p>
              <div className="sm:col-span-2"><Field label="Beneficiary name (as on passbook)"><input className={inp} value={f.bank_holder_name} onChange={(e) => set({ bank_holder_name: sanitizeName(e.target.value) })} /></Field></div>
              {/* Digits only — leading zeros are meaningful, so it stays a string. */}
              <Field label="Account number"><input className={inp} inputMode="numeric" value={f.account_number} onChange={(e) => set({ account_number: e.target.value.replace(/\D/g, '') })} /></Field>
              <Field label="Account type"><select className={inp} value={f.account_type} onChange={(e) => set({ account_type: e.target.value })}><option value="">—</option>{ACCOUNT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
              <div className="sm:col-span-2">
                <Field label="TDS applicable?">
                  {/* No default: the operator must consciously pick Yes or No
                      (finish() blocks submit otherwise). The consequence of the
                      choice is spelled out below the moment one is picked. */}
                  <div className="flex gap-4 text-sm">
                    <label className="flex items-center gap-1.5"><input type="radio" name="tds" checked={f.tds === 'yes'} onChange={() => { setFile('form_121', null); set({ tds: 'yes' }); }} /> Yes</label>
                    <label className="flex items-center gap-1.5"><input type="radio" name="tds" checked={f.tds === 'no'} onChange={() => { setFile('form_121', null); set({ tds: 'no' }); }} /> No</label>
                  </div>
                  {f.tds === 'yes' && (
                    <p className="text-xs text-text-muted mt-1.5">This customer will be charged 10% TDS on their monthly interest.</p>
                  )}
                  {f.tds === 'no' && (
                    <div className="mt-1.5">
                      <p className="text-xs text-text-muted">A Form 121 declaration should be submitted for TDS exemption. Upload it below (optional — you can add it later).</p>
                      <div className="mt-2 max-w-xs"><FilePick label="Form 121 (optional)" file={files.form_121} onPick={(x) => setFile('form_121', x)} /></div>
                    </div>
                  )}
                </Field>
              </div>
              <Field label="IFSC" hint={ifscState === 'looking' ? 'Looking up…' : ifscState === 'ok' ? '✓ Bank/branch filled' : ifscState === 'miss' ? 'Not found — enter bank/branch manually' : 'Auto-fills bank & branch'}>
                <input className={`${inp} uppercase`} placeholder="e.g. SBIN0001234" maxLength={11} value={f.ifsc} onChange={(e) => set({ ifsc: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11) })} />
                {f.ifsc.length === 11 && !IFSC_RE.test(f.ifsc) && <span className="text-[11px] text-danger">IFSC must be 4 letters, a 0, then 6 letters/digits — e.g. SBIN0001234.</span>}
              </Field>
              <Field label="Bank name"><input className={inp} value={f.bank_name} onChange={(e) => set({ bank_name: sanitizeName(e.target.value) })} /></Field>
              <Field label="Branch name"><input className={inp} value={f.branch_name} onChange={(e) => set({ branch_name: sanitizeName(e.target.value) })} /></Field>
              <Field label="Branch city"><input className={inp} value={f.branch_city} onChange={(e) => set({ branch_city: sanitizeName(e.target.value) })} /></Field>
              <div className="sm:col-span-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" disabled={pennyBusy || f.account_number.trim().length < 4 || !IFSC_RE.test(f.ifsc.trim())}
                    onClick={runPennyDrop} className="text-xs border border-border-strong rounded px-3 py-1.5 hover:bg-bg disabled:opacity-40">
                    {pennyBusy ? 'Verifying…' : '⛃ Penny-drop verify'}
                  </button>
                  {penny && (
                    <span className={`text-xs rounded px-2 py-0.5 ${penny.status === 'Verified' ? 'bg-[color:var(--success-bg)] text-success' : 'bg-[color:var(--danger-bg)] text-danger'}`}>
                      {penny.status === 'Verified' ? `✓ Verified${penny.name ? ' — ' + penny.name : ''}` : `✗ ${penny.detail ?? 'Not verified'}`}
                    </span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2"><FilePick label="Cheque / passbook image" hint="Shows account number + IFSC + name" file={files.bank_proof} onPick={(x) => setFile('bank_proof', x)} /></div>
            </div>
          )}

          {step === 4 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
              <p className="sm:col-span-2 text-xs text-text-muted -mb-1">Optional. Only the nominee's name is required if a nominee is being added.</p>
              <div className="sm:col-span-2"><Field label="Full name" hint="Required only if a nominee is being added"><input className={inp} value={f.nom_full_name} onChange={(e) => set({ nom_full_name: sanitizeName(e.target.value) })} /></Field></div>
              <Field label="Date of birth"><input className={inp} type="date" value={f.nom_dob} onChange={(e) => set({ nom_dob: e.target.value })} /></Field>
              <Field label="Relationship"><select className={inp} value={f.nom_relationship} onChange={(e) => set({ nom_relationship: e.target.value })}><option value="">—</option>{RELATIONSHIPS.map((r) => <option key={r}>{r}</option>)}</select></Field>
              <Field label="Nominee phone"><input className={inp} inputMode="numeric" maxLength={10} value={f.nom_phone} onChange={(e) => set({ nom_phone: e.target.value.replace(/\D/g, '') })} /></Field>
              <Field label="Nominee KYC" hint="Aadhaar or PAN of the nominee">
                <div className="flex gap-2">
                  <select className={`${inp} w-28`} value={f.nom_kyc_type} onChange={(e) => set({ nom_kyc_type: e.target.value })}><option value="">Type…</option><option value="Aadhaar">Aadhaar</option><option value="PAN">PAN</option></select>
                  <input className={inp} placeholder={f.nom_kyc_type === 'PAN' ? 'ABCDE1234F' : f.nom_kyc_type === 'Aadhaar' ? '12-digit Aadhaar' : 'ID number'}
                    value={f.nom_kyc_number} onChange={(e) => set({ nom_kyc_number: f.nom_kyc_type === 'PAN' ? e.target.value.toUpperCase() : e.target.value })} />
                </div>
              </Field>
              <div className="sm:col-span-2"><FilePick label="Nominee KYC photo" hint="Aadhaar / PAN scan of the nominee (image or PDF)" file={files.nominee_kyc} onPick={(x) => setFile('nominee_kyc', x)} /></div>
              <div className="sm:col-span-2">
                <Field label="Nominee address">
                  <label className="flex items-center gap-1.5 text-xs text-text-muted mb-1 cursor-pointer">
                    <input type="checkbox"
                      onChange={(e) => { if (e.target.checked) set({ nom_address: [f.address, f.city, f.district, f.state, f.pincode].map((x) => String(x || '').trim()).filter(Boolean).join(', ') }); }} />
                    Same as customer address
                  </label>
                  <textarea className={inp} rows={2} value={f.nom_address} onChange={(e) => set({ nom_address: e.target.value })} />
                </Field>
              </div>
              <Field label="Guardian name (if minor)"><input className={inp} value={f.guardian_name} onChange={(e) => set({ guardian_name: e.target.value })} /></Field>
              <Field label="Guardian PAN (if minor)"><input className={`${inp} uppercase`} value={f.guardian_pan} onChange={(e) => set({ guardian_pan: e.target.value.toUpperCase() })} /></Field>
            </div>
          )}

          {step === 5 && <Review f={f} files={files} />}
        </div>

        {err && <div className="text-xs text-danger px-5 pb-1">{err}</div>}
        {dup && (
          <div className="text-xs px-5 pb-2">
            Existing customer: <button className="font-mono text-primary underline" onClick={() => { onClose(); nav(`/app/customers/${dup.id}`); }}>{dup.customer_code}</button> ({dup.full_name}) — open them to book the new investment and use <b>Request handover</b> there (Admin/CXO/BM approve).
          </div>
        )}
        {partial && (
          <div className="text-xs px-5 pb-2 text-warn">
            Customer <button className="font-mono text-primary underline" onClick={() => { onClose(); nav(`/app/customers/${partial.id}`); }}>{partial.code}</button> was created, but something didn’t attach ({partial.reason}). Open them to add the remaining documents/details — do <b>not</b> save again (it already exists).
          </div>
        )}
        <div className="flex items-center justify-between gap-2 px-5 py-3.5 border-t border-border">
          <button onClick={() => (step === 0 ? onClose() : setStep(step - 1))} className="text-sm text-text-muted hover:underline px-3 py-1.5">
            {step === 0 ? 'Cancel' : '← Back'}
          </button>
          <div className="flex items-center gap-2">
            <button disabled={busy} onClick={() => finish(false)} className="text-sm border border-border-strong rounded px-4 py-1.5 hover:border-primary disabled:opacity-40">Save &amp; exit</button>
            {step < 5
              ? <button onClick={() => setStep(step + 1)} className="text-sm bg-primary hover:bg-primary-hover text-white rounded px-5 py-1.5 font-semibold">Next →</button>
              : <button disabled={busy} onClick={() => finish(true)} className="text-sm bg-primary hover:bg-primary-hover text-white rounded px-5 py-1.5 font-semibold disabled:opacity-40">Save &amp; add investment →</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Review ─────────────────────────────────────────────────────────────────
function Review({ f, files }: { f: Form; files: Record<DocKey, File | null> }) {
  const dash = (v: string) => (v && v.trim() ? v : '—');
  const docCount = Object.values(files).filter(Boolean).length;
  const Row = ({ k, v }: { k: string; v: string }) => (
    <div className="flex justify-between gap-4 py-1 border-b border-border/40 text-sm"><span className="text-text-muted">{k}</span><span className="text-right font-medium">{v}</span></div>
  );
  const Section = ({ title, children }: { title: string; children: ReactNode }) => (
    <div className="bg-bg rounded-lg p-4 mb-3">
      <div className="text-xs font-bold text-text-label uppercase tracking-wide mb-2">{title}</div>
      {children}
    </div>
  );
  return (
    <div>
      <p className="text-sm text-text-muted mb-3">Verify everything below. The customer is created <strong>live immediately</strong> — no separate approval. <strong>Save &amp; add investment</strong> opens their profile so you can add the investment straight away; the investment is what goes to the Approvals queue, where the approver reviews the customer profile and the investment together.</p>
      <Section title="Personal">
        <Row k="Name" v={dash(f.full_name)} /><Row k="Father's name" v={dash(f.father_name)} /><Row k="Occupation" v={dash(f.occupation)} />
        <Row k="DOB" v={dash(f.dob)} /><Row k="Gender" v={dash(f.gender)} /><Row k="PAN" v={dash(f.pan)} />
        <Row k="Aadhaar" v={dash(f.aadhaar)} /><Row k="Phone" v={dash(f.phone)} /><Row k="Alt phone" v={dash(f.phone_secondary)} />
        <Row k="Email" v={dash(f.email)} /><Row k="Category" v={dash(f.investor_category)} />
        <Row k="Address" v={[f.address, f.city, f.district, f.state].filter(Boolean).join(', ') || '—'} /><Row k="NRI" v={f.is_nri ? 'Yes' : 'No'} />
      </Section>
      <Section title="Demat">
        <Row k="Depository" v={dash(f.depository)} /><Row k="DP ID" v={dash(f.dp_id)} /><Row k="Client ID" v={dash(f.client_id)} />
      </Section>
      <Section title={`KYC docs (${docCount})`}>
        {docCount === 0 ? <div className="text-sm text-text-muted">No documents uploaded.</div>
          : Object.entries(files).filter(([, x]) => x).map(([k, x]) => <Row key={k} k={k.replace(/_/g, ' ')} v={x!.name} />)}
        <Row k="CKYC number" v={dash(f.ckyc_number)} />
      </Section>
      <Section title="Bank">
        <Row k="Beneficiary" v={dash(f.bank_holder_name)} /><Row k="Account" v={dash(f.account_number)} /><Row k="Type" v={dash(f.account_type)} />
        <Row k="TDS" v={f.tds === 'no' ? 'No — Form 121' : f.tds === 'yes' ? 'Yes — 10%' : '—'} /><Row k="IFSC" v={dash(f.ifsc)} />
        <Row k="Bank" v={dash(f.bank_name)} /><Row k="Branch" v={[f.branch_name, f.branch_city].filter(Boolean).join(', ') || '—'} />
      </Section>
      <Section title="Nominee">
        {f.nom_full_name.trim()
          ? <><Row k="Name" v={dash(f.nom_full_name)} /><Row k="Relationship" v={dash(f.nom_relationship)} /><Row k="DOB" v={dash(f.nom_dob)} /><Row k="KYC" v={f.nom_kyc_type ? `${f.nom_kyc_type} ${f.nom_kyc_number}`.trim() : '—'} /><Row k="Phone" v={dash(f.nom_phone)} /></>
          : <div className="text-sm text-text-muted">No nominee added.</div>}
      </Section>
    </div>
  );
}
